import { Config } from '../model/config';
import { OauthStates } from '../model/constants';
import { ERR_OIAM_GENERAL, ERR_UNMET, OiamErrorPayload } from '../model/errors';
import { NginxErrorLogger } from './nginx-error-logger';
import { parse } from './oidc-error-description-parser';
import { stripSpecialQueryparams } from './utils';

/**
 * Parses the query parameters for BA OIDC error messages and returns a suitable reaction.
 * This class has to be instantiated anew for each URL.
 */
export class OidcErrorService {
  private readonly url: URL;

  public constructor(
    private readonly authority: string,
    private readonly profileSelectionRedirectUrls: Config['profileSelectionRedirectUrls'],
    private readonly redirecturi: string,
    private readonly tdaUrl: string | undefined,
    url: string,
  ) {
    this.url = new URL(url);
  }

  /**
   * Check our URL for (custom) OIDC errors and handle them.
   * We have implemented our own protocol using unmet_authentication_requirements which can trigger follow-up
   * actions like redirecting to profile creation.
   * Purges the error message from the browser history before returning to avoid repeated handling
   * in cases of browser back or alike.
   * @returns false is returned when everything is fine and the regular flow can should continue. The other values have to be treated specifically.
   * When a URL is returned, the caller has to redirect there.
   */
  public checkForOidcErrors(): false | OidcRedirectResult | OiamErrorPayload | OidcChangeStateResult {
    const qp = this.url.searchParams;
    if (qp.has('error')) {
      const error = qp.get('error');
      const description = qp.get('error_description');

      // there might be unexpected behavior at browser back
      // to prevent it, remove the error from browser history
      this.removeErrorFromBrowserHistory();

      if (error === 'unmet_authentication_requirements' && description) {
        const ped = parse(description);
        // log at nginx for alerting
        NginxErrorLogger.logError({
          ...ERR_UNMET,
          details: ped.type,
        });
        switch (ped.type) {
          case 'freischaltung': {
            return { url: this.calcFreischaltungUrl() };
          }
          case 'baidmatch': {
            return {
              url: this.calcBaidmatchUrl(ped.actingOnBehalfOf, ped.identity, ped.actingOnBehalfOf),
            };
          }
          case 'profilanlage': {
            return { url: this.calcProfilanlageUrl(ped.baforprefix) };
          }
          case 'abbruch': {
            return { targetState: OauthStates.LOGGEDIN };
          }
          case 'reauth_required': {
            return { targetState: OauthStates.REAUTHREQUIRED };
          }
          case 'invalid_combination':
          case 'missing_session':
          case 'fehler': {
            return {
              ...ERR_OIAM_GENERAL,
              details: ped,
            };
          }
          default: {
            const exhaustiveCheck: never = ped;
            throw new Error(`Unexpected value: ${exhaustiveCheck}`);
          }
        }
      } else if (error === 'logout_requested') {
        return { targetState: OauthStates.LOGOUTREQUESTED };
      } else {
        const err = {
          ...ERR_OIAM_GENERAL,
          details: { error, description },
        };
        // log at nginx
        NginxErrorLogger.logError(err);
        return err;
      }
    }
    return false;
  }

  public calcFreischaltungUrl(): URL {
    const urlPattern =
      this.profileSelectionRedirectUrls[this.authority]?.freischaltung ??
      this.profileSelectionRedirectUrls.default.freischaltung;
    const url = urlPattern.replace('${CALLBACKURL}', this.callbackUrl());
    return new URL(url);
  }

  public calcProfilanlageUrl(baforprefix: string): URL {
    const [_, urlPattern] = [
      ...Object.entries(this.profileSelectionRedirectUrls[this.authority]?.profilanlage ?? {}),
      ...Object.entries(this.profileSelectionRedirectUrls.default.profilanlage),
    ].find(([bp, _]) => baforprefix.startsWith(bp));
    const urlString = urlPattern.replace('${CALLBACKURL}', this.callbackUrl());
    const url = new URL(urlString);
    if (this.tdaUrl) {
      url.searchParams.set('tdaurl', this.tdaUrl);
    }
    return url;
  }

  public calcBaidmatchUrl(bafor: string, identity: string, actingOnBehalfOf: string): URL {
    const [_, urlPattern] = [
      ...Object.entries(this.profileSelectionRedirectUrls[this.authority]?.baidmatch ?? {}),
      ...Object.entries(this.profileSelectionRedirectUrls.default.baidmatch),
    ].find(([bp, _]) => bafor.startsWith(bp));
    const url = urlPattern
      .replace('${CALLBACKURL}', this.callbackUrl())
      .replace('${IDENTITY}', identity)
      .replace('${ACTING_ON_BEHALF_OF}', actingOnBehalfOf);
    return new URL(url);
  }

  public static redirectTo(url: URL): void {
    // purge our OIDC protocol query parameters from the URL, so that a browser back won't see them again
    const u = stripSpecialQueryparams(window.location.href, {});
    window.history.replaceState(null, '', u);
    // now navigate away
    window.location.href = url.toString();
  }

  /**
   * true for error types supported by checkForOidcErrors.
   * Must be implemented consistently with this method!
   */
  public static isHandleableError = (e: string): boolean =>
    ['unmet_authentication_requirements', 'logout_requested'].includes(e);

  private callbackUrl(): string {
    // calling back redirecturi is fine because the original customstate
    // has to contain any special sub-route / params.
    const u = new URL(this.redirecturi);
    return encodeURIComponent(u.toString());
  }

  private removeErrorFromBrowserHistory() {
    this.url.searchParams.delete('error');
    this.url.searchParams.delete('error_description');
    window.history.replaceState(null, '', this.url);
  }
}

export interface OidcRedirectResult {
  url: URL;
}

export interface OidcChangeStateResult {
  /**
   * state which we should change to.
   * This list is artificially restricted to only those types for which we know they make sense
   * and are actually used.
   */
  targetState: OauthStates.LOGGEDIN | OauthStates.LOGOUTREQUESTED | OauthStates.REAUTHREQUIRED;
}
